/**
 * Public event overview.
 *
 * This is the page a share link lands on, so it must render for a visitor with
 * no session: it shows the event facts and a participant count, and offers the
 * entry points. The join action is the only one that needs a session, and for an
 * anonymous visitor it becomes the sign-in redirect so the visitor comes back to
 * this overview afterwards.
 *
 * A member may also ask for a recommendation round here, which is why the page
 * shows how much of the round budget is left: the server refuses a round that
 * would exceed it, and the member should be able to see that before clicking.
 */

import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { RelaxLimitsAction } from '../../components/event/relax-limits-action'
import { ErrorAlert, SkeletonCard } from '../../components/layout/states'
import { ParticipantList } from '../../components/shared/participant-list'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Stat, StatGrid } from '../../components/ui/primitives'
import {
  useDismissDepartureWarning,
  useDepartures,
  useRunSharedRecommendation,
  useSharedParticipants,
} from '../../hooks/use-shared'
import { useSharedRoundPace } from '../../hooks/use-round-pace'
import { ApiError } from '../../lib/api'
import { mutationErrorText } from '../../lib/mutation-error'
import { activityLabelKey, formatLocalDateTime } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { loginRedirectPath } from '../../lib/login-redirect'
import { useSharedOutlet } from './shared-layout'

export function SharedOverviewPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { shareToken, event, viewer } = useSharedOutlet()
  const participantsQuery = useSharedParticipants(shareToken)
  // Members only: a departure point may be a private address that a member chose,
  // so the section is not part of the anonymous share view.
  const canSeeDepartures = viewer.isOrganizer || viewer.isJoined
  const departuresQuery = useDepartures(shareToken, canSeeDepartures)
  const dismissWarning = useDismissDepartureWarning(shareToken)
  const runRecommendation = useRunSharedRecommendation(shareToken, event.id)
  // A round this event's history says runs long is queued instead of waited for:
  // the request then returns a row the round page polls, and the member may close
  // the tab without losing the work.
  const pace = useSharedRoundPace(shareToken)

  const budget = event.recommendationBudget
  const canTrigger = canSeeDepartures && event.status !== 'archived'
  const outOfBudget = budget !== null && budget.remaining <= 0
  // The alert reads the failure as text, so a request that never produced a
  // response still says something; the relax action below needs the `ApiError`
  // itself for the suggestion the engine attached to it.
  const runError = runRecommendation.error instanceof ApiError ? runRecommendation.error : null
  const runErrorText = mutationErrorText(runRecommendation.error, t)
  const dismissWarningError = mutationErrorText(dismissWarning.error, t)

  const pressure = departuresQuery.data?.pressure
  const departures = departuresQuery.data?.departures ?? []
  // The organizer keeps the reminder until the count actually falls; a member
  // sees it once and may dismiss it.
  const showWarning =
    Boolean(pressure?.warned) && (viewer.isOrganizer || !departuresQuery.data?.warningDismissed)

  const locale = currentLocale()
  const timeWindow = event.eventDateEnd
    ? `${formatLocalDateTime(event.eventDateStart, locale)} – ${formatLocalDateTime(event.eventDateEnd, locale)}`
    : formatLocalDateTime(event.eventDateStart, locale)

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="pt-4 sm:pt-5">
          <StatGrid>
            <Stat label={t('overview.time')} value={timeWindow} />
            {event.organizerNickname ? (
              <Stat label={t('overview.organizer')} value={event.organizerNickname} />
            ) : null}
            <Stat
              label={t('events.participants')}
              value={t('events.participantCount', { count: event.peopleCount })}
              hint={t('events.unitCount', { count: event.unitCount })}
            />
            <Stat label={t('eventNew.weatherLabel')} value={t(`weather.${event.weatherCondition}`)} />
          </StatGrid>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('events.activities')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-1.5">
          {event.activities.map((activity) => (
            <Badge key={activity} variant="secondary">
              {t(activityLabelKey(activity))}
            </Badge>
          ))}
        </CardContent>
      </Card>

      {showWarning && pressure ? (
        <Alert variant="warning">
          <AlertDescription>
            {t('overview.departureWarning', { count: pressure.count, limit: pressure.limit })}
          </AlertDescription>
          {viewer.isOrganizer ? null : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={dismissWarning.isPending}
              onClick={() => dismissWarning.mutate()}
            >
              {t('overview.departureWarningDismiss')}
            </Button>
          )}
          {/* A dismissal that failed leaves the warning on screen, so it has to say
              why instead of looking like a button that does nothing. */}
          {dismissWarningError ? (
            <AlertDescription>{dismissWarningError}</AlertDescription>
          ) : null}
        </Alert>
      ) : null}

      {event.status === 'archived' ? (
        <Alert variant="info">
          <AlertDescription>{t('events.archivedHint')}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-wrap gap-3">
        {event.status === 'archived' ? null : viewer.isJoined ? (
          <Button variant="outline" asChild>
            <Link to={`/e/${shareToken}/join`}>{t('join.update')}</Link>
          </Button>
        ) : (
          <Button asChild>
            <Link
              to={
                viewer.isAuthenticated
                  ? `/e/${shareToken}/join`
                  : // Come back to the wizard itself after signing in, not to the
                    // overview the visitor is already looking at.
                    loginRedirectPath(`/e/${shareToken}/join`, '')
              }
            >
              {t('overview.joinAction')}
            </Link>
          </Button>
        )}
        <Button variant="outline" asChild>
          <Link to={`/e/${shareToken}/recommendations`}>{t('overview.recommendAction')}</Link>
        </Button>
        {canTrigger ? (
          <Button
            disabled={runRecommendation.isPending || outOfBudget}
            onClick={() =>
              runRecommendation.mutate(
                { background: pace.isSlow },
                {
                  // A queued round is a row with a progress counter, so the reader
                  // is sent to that row rather than to the list: the list would
                  // show a round with nothing in it yet.
                  onSuccess: (result) =>
                    navigate(
                      result.pending
                        ? `/e/${shareToken}/recommendations/${result.round.round}`
                        : `/e/${shareToken}/recommendations`,
                    ),
                },
              )
            }
          >
            {runRecommendation.isPending ? t('overview.runningRecommend') : t('overview.triggerRecommend')}
          </Button>
        ) : null}
      </div>

      {canTrigger && pace.isSlow ? (
        <p className="text-muted-foreground text-xs">{t('overview.backgroundRunsHint')}</p>
      ) : null}

      {canTrigger && budget ? (
        <p className="text-muted-foreground text-xs">
          {outOfBudget
            ? t('overview.roundBudgetExhausted')
            : t('overview.roundBudgetRemaining', {
                remaining: budget.remaining,
                eventLimit: budget.eventLimit,
                memberLimit: budget.memberLimit,
              })}
        </p>
      ) : null}

      {runErrorText ? (
        <ErrorAlert message={runErrorText} />
      ) : null}

      <RelaxLimitsAction
        error={runError}
        isPending={runRecommendation.isPending}
        onRelax={(minutes) =>
          runRecommendation.mutate(
            { relaxedTravelMinutes: minutes, background: pace.isSlow },
            {
              onSuccess: (result) =>
                navigate(
                  result.pending
                    ? `/e/${shareToken}/recommendations/${result.round.round}`
                    : `/e/${shareToken}/recommendations`,
                ),
            },
          )
        }
      />

      {viewer.isAuthenticated ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('events.participants')}</CardTitle>
          </CardHeader>
          <CardContent>
            {participantsQuery.isPending ? (
              <SkeletonCard />
            ) : (
              <ParticipantList participants={participantsQuery.data?.participants ?? []} />
            )}
          </CardContent>
        </Card>
      ) : (
        <Alert variant="info">
          <AlertDescription>{t('overview.anonymousHint')}</AlertDescription>
        </Alert>
      )}

      {canSeeDepartures && departures.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('overview.departures')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {departures.map((departure) => (
              <div key={departure.id} className="flex flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{departure.label}</span>
                  <Badge variant="secondary">
                    {t('overview.departureHeadcount', { count: departure.headcount })}
                  </Badge>
                  {departure.id === departuresQuery.data?.myGroupId ? (
                    <Badge>{t('overview.myDeparture')}</Badge>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {departure.members.map((member) => (
                    <Badge key={member.id} variant="outline">
                      {member.headcount > 1
                        ? t('overview.departureMemberWithCount', {
                            nickname: member.nickname,
                            count: member.headcount,
                          })
                        : member.nickname}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
